import {
  SignedIn,
  SignedOut,
  useAuth,
  useClerk,
  useOrganization,
  useOrganizationList,
} from '@clerk/clerk-react';
import type { UserRole } from '@ergshoot/shared';
import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { SessionExpiryHandler } from './components/auth/SessionExpiryHandler';
import { UserSyncProvider, useUserSyncContext } from './components/auth/UserSyncProvider/index';
import { useLeaderboardSync } from './hooks/useLeaderboardSync';
import {
  findProfileForRole,
  getRememberedProfileId,
  getRouteEligibleRoles,
  getRouteTargetRole,
  resolveAuthRoutingState,
} from './lib/authRouting';
import {
  buildLoginPath,
  getCurrentDestination,
  hasAuthenticatedSession,
  signOutExplicitly,
} from './lib/authSession';
import { useAuthStore } from './stores/authStore';

/**
 * Error boundary to catch React errors and prevent white screen crashes.
 */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="text-center max-w-md">
              <h2 className="text-xl font-bold text-destructive mb-2">Something went wrong</h2>
              <p className="text-muted-foreground mb-4">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Reload Page
              </button>
            </div>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

/**
 * Auto-select organization if user has memberships but no active org.
 * This prevents blank screen when Clerk Organizations is enabled.
 */
function AutoSelectOrganization() {
  const { setActive } = useClerk();
  const { organization } = useOrganization();
  const { userMemberships, isLoaded } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  useEffect(() => {
    if (!isLoaded) return;

    // Wrap in try/catch to prevent crashes
    try {
      // If no active org but user has memberships, auto-select the first one
      if (!organization && userMemberships.data && userMemberships.data.length > 0) {
        const firstOrg = userMemberships.data[0]?.organization;
        if (firstOrg?.id) {
          console.log('[Auth] Auto-selecting organization:', firstOrg.name);
          setActive({ organization: firstOrg.id }).catch((err) => {
            console.error('[Auth] Failed to set active organization:', err);
          });
        }
      }
    } catch (err) {
      console.error('[Auth] Error in AutoSelectOrganization:', err);
    }
  }, [isLoaded, organization, userMemberships.data, setActive]);

  return null; // This component just runs the effect
}

// Auth pages
import AcceptInvite from './pages/auth/AcceptInvite';
import ForgotPassword from './pages/auth/ForgotPassword';
import Login from './pages/auth/Login';
import ProfileSelection from './pages/auth/ProfileSelection';
import Register from './pages/auth/Register';
import SubscriptionChoice from './pages/auth/SubscriptionChoice';

import AffiliateApplication from './pages/public/AffiliateApplication';
import AgentInquiry from './pages/public/AgentInquiry';
import FindGym from './pages/public/FindGym';
import IndividualSignup from './pages/public/IndividualSignup';
// Public pages
import Landing from './pages/public/Landing';
import PrivacyPolicy from './pages/public/PrivacyPolicy';

import Settings from './pages/Settings';
// Portal pages
import AdminDashboard from './pages/admin/Dashboard';
import AffiliateDashboard from './pages/affiliate/Dashboard';
import AgentDashboard from './pages/agent/Dashboard';
import IndividualDashboard from './pages/individual/Dashboard';
import MemberDashboard from './pages/member/Dashboard';
// Public display pages
import TVDisplay from './pages/shared/TVDisplay';
import TVLeaderboard from './pages/shared/TVLeaderboard';

import { MinimizedWorkoutPill } from './components/programming/MinimizedWorkoutPill';
// Shared pages (accessible from unified nav)
import IndividualProgress from './pages/individual/IndividualProgress';
import Assets from './pages/shared/Assets';
import Billing from './pages/shared/Billing';
import Community from './pages/shared/Community';
import Events from './pages/shared/Events';
import Leaderboard from './pages/shared/Leaderboard';
import Manuals from './pages/shared/Manuals';
import MyHub from './pages/shared/MyHub';
import MyTeam from './pages/shared/MyTeam';
import News from './pages/shared/News';
import Programming from './pages/shared/Programming';
import SharedLayout from './pages/shared/SharedLayout';
import TheStandards from './pages/shared/TheStandards';

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

// Error display component for auth issues
function AuthError({ message }: { message: string }) {
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOutExplicitly(signOut);
    navigate('/');
  };

  const isSuspended = message === 'ACCOUNT_SUSPENDED';
  const isAccountMissing = message === 'ACCOUNT_NOT_FOUND';
  const isTimeout = message.includes('timed out') || message.includes('Failed to fetch');
  const title = isSuspended
    ? 'Account Suspended'
    : isAccountMissing
      ? 'Account Setup Issue'
      : isTimeout
        ? 'Connection Issue'
        : 'Authentication Error';
  const body = isSuspended
    ? 'Your account has been suspended. Please contact HQ for more information.'
    : isAccountMissing
      ? 'You are signed in, but we could not find your ErgShoot profile. Please retry or contact support so we can restore your access.'
      : isTimeout
        ? 'Could not reach the server. Please check your connection and try again.'
        : message;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8 max-w-md">
        <div className="text-destructive text-lg font-semibold mb-2">{title}</div>
        <p className="text-muted-foreground mb-4">{body}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {!isSuspended && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Retry
            </button>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            className="px-4 py-2 border border-border rounded hover:bg-muted"
          >
            Sign Out
          </button>
          {isAccountMissing && (
            <a
              href="mailto:support@ergshoot.com?subject=Missing%20ErgShoot%20profile"
              className="px-4 py-2 text-primary hover:underline"
            >
              Contact Support
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function NoAccess() {
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOutExplicitly(signOut);
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8 max-w-md">
        <div className="text-destructive text-lg font-semibold mb-2">No Portal Access</div>
        <p className="text-muted-foreground mb-4">
          This account does not currently have an active portal role. Contact ErgShoot support if
          you believe this is incorrect.
        </p>
        <button
          type="button"
          onClick={handleSignOut}
          className="px-4 py-2 border border-border rounded hover:bg-muted"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

/**
 * Protected route wrapper - redirects to login if not authenticated.
 * Wraps SignedIn/SignedOut to prevent black screen for unauthenticated users.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <SignedOutRedirect />
      </SignedOut>
    </>
  );
}

function SignedOutRedirect() {
  const location = useLocation();
  const expired = hasAuthenticatedSession();
  return (
    <Navigate
      to={buildLoginPath(getCurrentDestination(location), expired)}
      replace
      state={{ from: location }}
    />
  );
}

// Role-based route wrapper - uses synced user from authStore
// Respects the role toggle bar: when a single role is toggled,
// only that role's portal is accessible (prevents admin pages showing for Individual toggle)
function RoleRoute({
  allowedRoles,
  children,
}: {
  allowedRoles: UserRole[];
  children: React.ReactNode;
}) {
  const { isLoading, user, error } = useUserSyncContext();
  const sessionActiveRoles = useAuthStore((s) => s.sessionActiveRoles);
  const profiles = useAuthStore((s) => s.profiles);
  const activeProfile = useAuthStore((s) => s.activeProfile);
  const setSessionActiveRoles = useAuthStore((s) => s.setSessionActiveRoles);
  const switchProfile = useAuthStore((s) => s.switchProfile);
  const [routeSwitchError, setRouteSwitchError] = React.useState<string | null>(null);
  const [switchingProfileId, setSwitchingProfileId] = React.useState<string | null>(null);

  const routing = resolveAuthRoutingState({ user, profiles, activeProfile, sessionActiveRoles });
  const eligibleRoles =
    routing.status === 'missing-role'
      ? []
      : getRouteEligibleRoles(routing.availableRoles, sessionActiveRoles);
  const targetRole =
    routing.status === 'missing-role'
      ? null
      : getRouteTargetRole(allowedRoles, routing.role, eligibleRoles);
  const targetProfile = targetRole
    ? findProfileForRole(profiles, targetRole, activeProfile, getRememberedProfileId(targetRole))
    : null;

  useEffect(() => {
    if (routing.status === 'stale-session-role') {
      setSessionActiveRoles(null);
    }
  }, [routing.status, setSessionActiveRoles]);

  useEffect(() => {
    if (routing.status === 'missing-role') return;
    if (!targetProfile || activeProfile?.id === targetProfile.id) return;
    if (switchingProfileId === targetProfile.id) return;

    setRouteSwitchError(null);
    setSwitchingProfileId(targetProfile.id);
    switchProfile(targetProfile.id)
      .catch((switchError) => {
        console.error('Failed to switch profile for route:', switchError);
        setRouteSwitchError('Failed to switch profile for this portal.');
      })
      .finally(() => {
        setSwitchingProfileId(null);
      });
  }, [activeProfile?.id, routing.status, switchProfile, switchingProfileId, targetProfile]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // If user exists in Clerk but not in D1, redirect to complete registration
  if (error === 'PROFILE_NOT_FOUND') {
    return <Navigate to="/select-role" replace />;
  }

  // Show error message if auth sync failed (actual errors, not profile not found)
  if (error) {
    return <AuthError message={error} />;
  }

  if (routing.status === 'missing-role') {
    return <Navigate to={routing.path} replace />;
  }

  if (routeSwitchError) {
    return <AuthError message={routeSwitchError} />;
  }

  if (targetProfile && activeProfile?.id !== targetProfile.id) {
    return <LoadingSpinner />;
  }

  if (targetRole && allowedRoles.includes(targetRole)) {
    return <>{children}</>;
  }

  if (!targetRole) {
    return <Navigate to={routing.path} replace />;
  }

  return <LoadingSpinner />;
}

// Dashboard router based on role from D1 database
function DashboardRouter() {
  const { isLoading, isClerkLoaded, isSignedIn, user, error } = useUserSyncContext();
  const profiles = useAuthStore((s) => s.profiles);
  const activeProfile = useAuthStore((s) => s.activeProfile);
  const sessionActiveRoles = useAuthStore((s) => s.sessionActiveRoles);
  const setSessionActiveRoles = useAuthStore((s) => s.setSessionActiveRoles);

  const routing = resolveAuthRoutingState({ user, profiles, activeProfile, sessionActiveRoles });

  useEffect(() => {
    if (routing.status === 'stale-session-role') {
      setSessionActiveRoles(null);
    }
  }, [routing.status, setSessionActiveRoles]);

  // Wait for Clerk to load and session to be recognized
  if (!isClerkLoaded || isLoading) {
    return <LoadingSpinner />;
  }

  // If Clerk loaded but not signed in, redirect to landing page
  if (!isSignedIn) {
    return <Navigate to="/" replace />;
  }

  // If user exists in Clerk but not in D1, redirect to complete registration
  if (error === 'PROFILE_NOT_FOUND') {
    return <Navigate to="/select-role" replace />;
  }

  // If actual error fetching user, show error with options
  if (error) {
    return <AuthError message={error} />;
  }

  // Multi-profile users: show profile selection screen if no session choice made yet
  // Check sessionStorage directly to survive page refreshes within same tab
  const hasSessionChoice =
    sessionActiveRoles !== null || sessionStorage.getItem('ergshoot_session_roles') !== null;
  if (profiles.length > 1 && !hasSessionChoice) {
    return <Navigate to="/profile-select" replace />;
  }

  if (routing.status === 'missing-role') {
    return <Navigate to={routing.path} replace />;
  }

  return <Navigate to={routing.path} replace />;
}

function App() {
  const { isLoaded } = useAuth();
  useLeaderboardSync();

  // Don't render any routes until Clerk has determined auth state
  // This prevents login/register page flash for authenticated users
  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  return (
    <UserSyncProvider>
      <SessionExpiryHandler />
      {/* Auto-select organization for users with memberships */}
      <SignedIn>
        <AutoSelectOrganization />
      </SignedIn>
      <Routes>
        {/* Public auth routes - Clerk handles these */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/select-role" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/profile-select" element={<ProfileSelection />} />
        <Route path="/choose-subscription" element={<SubscriptionChoice />} />
        <Route path="/invite/:inviteId" element={<AcceptInvite />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Public signup routes */}
        <Route path="/signup/individual" element={<IndividualSignup />} />
        <Route path="/apply/affiliate" element={<AffiliateApplication />} />
        <Route path="/find-gym" element={<FindGym />} />
        <Route path="/apply/agent" element={<AgentInquiry />} />

        {/* Public legal pages (no auth — required for Stripe) */}
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Public TV display (no auth required) */}
        <Route path="/tv/:workoutId" element={<TVDisplay />} />
        <Route path="/tv/leaderboard" element={<TVLeaderboard />} />

        {/* Legacy routes - redirect to new paths */}
        <Route path="/auth/login" element={<Navigate to="/login" replace />} />
        <Route path="/auth/register" element={<Navigate to="/register" replace />} />

        {/* Protected routes - redirect to login if not authenticated */}
        <Route
          path="/agent/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={['agent']}>
                <AgentDashboard />
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={['affiliate_owner', 'affiliate_coach']}>
                <AffiliateDashboard />
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/member/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={['member']}>
                <MemberDashboard />
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={['hq_admin']}>
                <AdminDashboard />
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/individual/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={['individual']}>
                <IndividualDashboard />
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Settings route - accessible to all authenticated users */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <RoleRoute
                allowedRoles={[
                  'hq_admin',
                  'agent',
                  'affiliate_owner',
                  'affiliate_coach',
                  'member',
                  'individual',
                ]}
              >
                <Settings />
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Shared routes - accessible to all authenticated users with layout */}
        <Route
          element={
            <ProtectedRoute>
              <RoleRoute
                allowedRoles={[
                  'hq_admin',
                  'agent',
                  'affiliate_owner',
                  'affiliate_coach',
                  'member',
                  'individual',
                ]}
              >
                <SharedLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route path="/hub" element={<MyHub />} />
          <Route path="/team" element={<MyTeam />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/community" element={<Community />} />
          <Route path="/events" element={<Events />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/news" element={<News />} />
          <Route path="/workouts" element={<Programming />} />
          <Route path="/standards" element={<TheStandards />} />
          <Route path="/progress" element={<IndividualProgress />} />
        </Route>

        {/* Assets & Manuals — staff only (hq_admin, agent, affiliate_owner, affiliate_coach) */}
        {/* Members and individuals do NOT have access — confirmed March 3 scope sheet */}
        <Route
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={['hq_admin', 'agent', 'affiliate_owner', 'affiliate_coach']}>
                <SharedLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route path="/resources/assets" element={<Assets />} />
          <Route path="/resources/manuals" element={<Manuals />} />
        </Route>

        {/* Dashboard route - always runs DashboardRouter (used as post-login redirect) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/no-access"
          element={
            <ProtectedRoute>
              <NoAccess />
            </ProtectedRoute>
          }
        />

        {/* Default route */}
        <Route
          path="/"
          element={
            <>
              <SignedIn>
                <DashboardRouter />
              </SignedIn>
              <SignedOut>
                <Landing />
              </SignedOut>
            </>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <MinimizedWorkoutPill />
    </UserSyncProvider>
  );
}

// Wrap App with ErrorBoundary to catch crashes
function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
